import { memo } from 'react';

import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';

import { formatHours } from '../../../helpers/format-time-track.helper';
import { ApprovedValueLabel } from './approved-value-label';

interface ApprovalAggregateHoursProps {
  hours: number;
  /** Render in a heavier weight (used for top-level project rows). */
  emphasized?: boolean;
}

/**
 * Formatted hours for the aggregate (member/project) rows of the Tracked
 * and Booked columns. Project rows use a heavier weight than members.
 */
function ApprovalAggregateHoursComponent({
  hours,
  emphasized = false,
}: ApprovalAggregateHoursProps) {
  return <span className={emphasized ? 'font-medium' : undefined}>{formatHours(hours)}</span>;
}

/** Memoized so an unrelated row's selection toggle doesn't re-render every hours cell. */
export const ApprovalAggregateHours = memo(ApprovalAggregateHoursComponent);

interface ApprovalAggregateApprovedHoursProps {
  approvedForInvoiceHours: number;
  approvedForBillHours: number;
  /** Render in a heavier weight (used for top-level project rows). */
  emphasized?: boolean;
}

/**
 * The Approved cell of an aggregate (member/project) row: the two approved totals stacked with the
 * same `Inv` / `Bill` micro-labels the editable worklog cell uses, so a reader scanning the column
 * sees one consistent shape at every level.
 */
function ApprovalAggregateApprovedHoursComponent({
  approvedForInvoiceHours,
  approvedForBillHours,
  emphasized = false,
}: ApprovalAggregateApprovedHoursProps) {
  const valueClassName = cn('tabular-nums', emphasized && 'font-medium');

  return (
    <div className="flex flex-col gap-0.5 text-xs leading-tight">
      <span className="flex items-center gap-1">
        <ApprovedValueLabel
          tooltip={i18n.t('authenticated.timeTracking:approvals.columns.approvedInvoiceTooltip')}
        >
          {i18n.t('authenticated.timeTracking:approvals.columns.approvedInvoiceLabel')}
        </ApprovedValueLabel>
        <span className={valueClassName}>{formatHours(approvedForInvoiceHours)}</span>
      </span>
      <span className="flex items-center gap-1">
        <ApprovedValueLabel
          tooltip={i18n.t('authenticated.timeTracking:approvals.columns.approvedBillTooltip')}
        >
          {i18n.t('authenticated.timeTracking:approvals.columns.approvedBillLabel')}
        </ApprovedValueLabel>
        <span className={valueClassName}>{formatHours(approvedForBillHours)}</span>
      </span>
    </div>
  );
}

/** Memoized for the same reason as {@link ApprovalAggregateHours}. */
export const ApprovalAggregateApprovedHours = memo(ApprovalAggregateApprovedHoursComponent);
